import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Topbar from '../components/Topbar';
import axios from '../libs/axios';

export default function Logs({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    skip: 0,
    limit: 50,
    status_code: '',
    method: '',
    environment: '',
    error_only: false,
    log_type: '' // 'api', 'error', 'system'
  });
  const [total, setTotal] = useState(0);
  const [expandedLogs, setExpandedLogs] = useState({});

  const fetchLogs = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.skip) params.skip = filters.skip;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status_code) params.status_code = parseInt(filters.status_code);
      if (filters.method) params.method = filters.method;
      if (filters.environment) params.environment = filters.environment;
      if (filters.error_only) params.error_only = true;
      if (filters.log_type) params.log_type = filters.log_type;

      const res = await axios.get('/logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [token, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      skip: 0 // Reset pagination when filters change
    }));
  };

  const handlePageChange = (direction) => {
    setFilters(prev => ({
      ...prev,
      skip: direction === 'next' 
        ? Math.min(prev.skip + prev.limit, Math.max(0, total - prev.limit))
        : Math.max(0, prev.skip - prev.limit)
    }));
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getStatusBadgeClass = (statusCode, method) => {
    // System logs don't have status codes
    if (method === 'SYSTEM') return 'status-system';

    if (!statusCode) return 'status-unknown';
    if (statusCode >= 200 && statusCode < 300) return 'status-success';
    if (statusCode >= 400 && statusCode < 500) return 'status-client-error';
    if (statusCode >= 500) return 'status-server-error';
    return 'status-unknown';
  };

  const getLogType = (log) => {
    // Use the logType field from backend if available
    if (log.logType) return log.logType;
    
    // Fallback to old logic for backward compatibility
    if (log.method === 'SYSTEM') return 'system';
    if (log.errorMessage || (log.statusCode && log.statusCode >= 400)) return 'error';
    return 'api';
  };

  const getLogTypeIcon = (logType) => {
    switch (logType) {
      case 'error': return '❌';
      case 'system': return '🔧';
      case 'job': return '📋';
      default: return '📡';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return `${(duration * 1000).toFixed(0)}ms`;
  };

  if (!token) {
    return (
      <div className="landing">
        <div className="landing-inner">
          <h1>API Logs</h1>
          <p>Please sign in to view API logs.</p>
          <div className="actions">
            <Link className="btn-primary" to="/login">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Topbar />
      <main className="content">
        <div className="flex items-center justify-between mb-4">
          <h2>DATEV API Activity Logs</h2>
          <div className="flex gap-2 text-sm">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
              API Calls
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-100 border border-red-300 rounded"></span>
              Errors
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></span>
              System
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-purple-100 border border-purple-300 rounded"></span>
              Job Logs
            </span>
          </div>
        </div>

        {error && <div className="alert error">{error}</div>}

        {/* Filters */}
        <div className="card mb-4">
          <h3>Filters</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label>Status Code:</label>
              <input
                type="number"
                value={filters.status_code}
                onChange={(e) => handleFilterChange('status_code', e.target.value)}
                placeholder="e.g., 200, 404"
                className="form-input"
              />
            </div>
            <div>
              <label>Method:</label>
              <select
                value={filters.method}
                onChange={(e) => handleFilterChange('method', e.target.value)}
                className="form-select"
              >
                <option value="">All Methods</option>
                <option value="SYSTEM">SYSTEM</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label>Environment:</label>
              <select
                value={filters.environment}
                onChange={(e) => handleFilterChange('environment', e.target.value)}
                className="form-select"
              >
                <option value="">All Environments</option>
                <option value="sandbox">Sandbox</option>
                <option value="prod">Production</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label>Log Type:</label>
              <select
                value={filters.log_type}
                onChange={(e) => handleFilterChange('log_type', e.target.value)}
                className="form-select"
              >
                <option value="">All Types</option>
                <option value="api">API Calls</option>
                <option value="error">Errors</option>
                <option value="system">System</option>
                <option value="job">Job Logs</option>
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.error_only}
                  onChange={(e) => handleFilterChange('error_only', e.target.checked)}
                  className="mr-2"
                />
                Errors Only (4XX/5XX)
              </label>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="muted">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="muted">No API logs found.</div>
        ) : (
          <div className="card">
            <div className="mb-4 text-sm text-gray-600">
              Showing {filters.skip + 1}-{Math.min(filters.skip + filters.limit, total)} of {total} logs
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Time</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Type</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Method</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>URL</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Duration</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Environment</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const logType = getLogType(log);
                    return (
                      <React.Fragment key={log.id}>
                        <tr className={logType === 'error' ? 'bg-red-50' : logType === 'system' ? 'bg-blue-50' : logType === 'job' ? 'bg-purple-50' : ''}>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <span className="flex items-center gap-1">
                              {getLogTypeIcon(logType)}
                              <span className="capitalize text-xs font-medium">
                                {logType}
                              </span>
                            </span>
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <span className="font-mono">{log.method}</span>
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <div className="truncate max-w-xs" title={log.url}>
                              {log.url || log.requestBody?.substring(0, 50) + '...'}
                            </div>
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <div className="flex flex-col gap-1">
                              <span className={`status-badge ${getStatusBadgeClass(log.statusCode, log.method)}`}>
                                {log.statusCode || 'N/A'}
                              </span>
                              {(log.statusCode >= 400 && log.statusCode < 600) && (
                                <span className="text-xs text-yellow-700 font-medium">
                                  ⚠️ Contact help@admintools.ai
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            {formatDuration(log.duration)}
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <span className="capitalize">{log.environment || 'N/A'}</span>
                          </td>
                          <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <button
                              className="btn-secondary btn-sm"
                              onClick={() => toggleLogExpansion(log.id)}
                            >
                              {expandedLogs[log.id] ? 'Collapse' : 'Details'}
                            </button>
                          </td>
                        </tr>

                        {expandedLogs[log.id] && (
                          <tr>
                            <td colSpan={8} style={{ padding: 16, backgroundColor: '#f8f9fa' }}>
                              <div className="space-y-4">
                                {/* Simple API Log Display */}
                                {logType === 'api' && (
                                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                      <span>📡</span> API Call Details
                                    </h4>
                                    
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <p><strong>When:</strong> <span className="font-mono">{new Date(log.createdAt).toLocaleString()}</span></p>
                                        <p><strong>Method:</strong> <span className="font-mono bg-blue-100 px-2 py-1 rounded">{log.method}</span></p>
                                        <p><strong>URL:</strong> <span className="font-mono text-blue-600 break-all">{log.url}</span></p>
                                      </div>
                                      <div>
                                        <p><strong>Response Code:</strong> 
                                          <span className={`ml-2 px-2 py-1 rounded font-mono ${
                                            log.statusCode >= 200 && log.statusCode < 300 ? 'bg-green-200 text-green-800' :
                                            log.statusCode >= 400 && log.statusCode < 500 ? 'bg-yellow-200 text-yellow-800' :
                                            log.statusCode >= 500 ? 'bg-red-200 text-red-800' :
                                            'bg-gray-200 text-gray-800'
                                          }`}>
                                            {log.statusCode || 'N/A'}
                                          </span>
                                        </p>
                                        <p><strong>Duration:</strong> <span className="font-mono">{formatDuration(log.duration)}</span></p>
                                      </div>
                                    </div>

                                    {log.errorMessage && (
                                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                                        <p className="text-red-800 font-medium">Error: {log.errorMessage}</p>
                                      </div>
                                    )}

                                    {(log.statusCode >= 400 && log.statusCode < 600) && (
                                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                        <p className="text-yellow-800 font-medium">
                                          ⚠️ Please contact help@admintools.ai
                                        </p>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <h5 className="font-medium mb-2 text-gray-700">Request Headers:</h5>
                                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto max-h-32">
                                          <pre>{log.requestHeaders ? JSON.stringify(log.requestHeaders, null, 2) : 'No headers'}</pre>
                                        </div>
                                      </div>
                                      <div>
                                        <h5 className="font-medium mb-2 text-gray-700">Response Headers:</h5>
                                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto max-h-32">
                                          <pre>{log.responseHeaders ? JSON.stringify(log.responseHeaders, null, 2) : 'No headers'}</pre>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                      <div>
                                        <h5 className="font-medium mb-2 text-gray-700">Request Body:</h5>
                                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto max-h-32">
                                          <pre className="whitespace-pre-wrap">{log.requestBody || 'No body'}</pre>
                                        </div>
                                      </div>
                                      <div>
                                        <h5 className="font-medium mb-2 text-gray-700">Response Body:</h5>
                                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto max-h-32">
                                          <pre className="whitespace-pre-wrap">{log.responseBody || 'No body'}</pre>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Simple Job Log Display */}
                                {logType === 'job' && (
                                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                      <span>📋</span> Job Log Details
                                    </h4>
                                    
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <p><strong>When:</strong> <span className="font-mono">{new Date(log.createdAt).toLocaleString()}</span></p>
                                        <p><strong>Level:</strong> <span className="font-mono bg-purple-100 px-2 py-1 rounded">{log.level}</span></p>
                                        {log.job && <p><strong>Job:</strong> <span className="font-mono text-purple-600">{log.job.name}</span></p>}
                                      </div>
                                      <div>
                                        <p><strong>Job ID:</strong> <span className="font-mono text-purple-600">{log.jobId}</span></p>
                                      </div>
                                    </div>

                                    <div>
                                      <h5 className="font-medium mb-2 text-gray-700">Message:</h5>
                                      <div className="bg-gray-100 p-2 rounded text-sm">
                                        <p>{log.requestBody}</p>
                                      </div>
                                    </div>

                                    {log.responseBody && (
                                      <div className="mt-4">
                                        <h5 className="font-medium mb-2 text-gray-700">Details:</h5>
                                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                          <pre className="whitespace-pre-wrap">{log.responseBody}</pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Simple System Log Display */}
                                {logType === 'system' && (
                                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                      <span>🔧</span> System Message
                                    </h4>
                                    
                                    <div className="mb-4">
                                      <p><strong>When:</strong> <span className="font-mono">{new Date(log.createdAt).toLocaleString()}</span></p>
                                    </div>

                                    <div>
                                      <h5 className="font-medium mb-2 text-gray-700">Message:</h5>
                                      <div className="bg-gray-100 p-2 rounded text-sm">
                                        <pre className="whitespace-pre-wrap">{log.requestBody}</pre>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <button
                className="btn-secondary"
                onClick={() => handlePageChange('prev')}
                disabled={filters.skip === 0}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {Math.floor(filters.skip / filters.limit) + 1} of {Math.ceil(total / filters.limit)}
              </span>
              
              <button
                className="btn-secondary"
                onClick={() => handlePageChange('next')}
                disabled={filters.skip + filters.limit >= total}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
